
import { useState, useEffect } from 'react';
import { usePrayerStore } from '@/hooks/usePrayerStore';
import { usePrayerActions } from '@/hooks/usePrayerActions';
import type { Prayer } from '@/types/prayer';

export function usePrayersPage() {
  const {
    userPrayers,
    communityPrayers,
    favoritePrayers,
    bookmarkedPrayers,
    refreshPrayers,
    isLoading,
    error
  } = usePrayerStore();
  
  const { toggleFavorite, toggleBookmark } = usePrayerActions();

  // State for UI controls
  const [activeTab, setActiveTab] = useState<'my' | 'community' | 'favorites' | 'bookmarks'>('my');

  // Get current prayers based on active tab
  const getCurrentPrayers = (): Prayer[] => {
    switch (activeTab) {
      case 'my':
        return userPrayers;
      case 'community':
        return communityPrayers;
      case 'favorites':
        return favoritePrayers;
      case 'bookmarks':
        return bookmarkedPrayers;
      default:
        return userPrayers;
    }
  };

  // Action handlers with proper integration
  const handlePray = async (id: string) => {
    // TODO: Implement prayer commitment functionality
    console.log('Praying for prayer:', id);
  };

  // Updated to use real toggleFavorite from prayer store with error handling
  const handleLike = async (id: string) => {
    console.log('Toggling favorite for prayer:', id);
    try {
      const success = await toggleFavorite(id);
      if (!success) {
        console.error('Failed to toggle favorite for prayer:', id);
      } else {
        console.log('Successfully toggled favorite for prayer:', id);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const handleComment = (id: string) => {
    // TODO: Implement comment functionality
    console.log('Opening comments for prayer:', id);
  };

  // Updated to use real toggleBookmark from prayer store with error handling
  const handleBookmark = async (id: string) => {
    console.log('Toggling bookmark for prayer:', id);
    try {
      const success = await toggleBookmark(id);
      if (!success) {
        console.error('Failed to toggle bookmark for prayer:', id);
      } else {
        console.log('Successfully toggled bookmark for prayer:', id);
      }
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  };

  const handleRefresh = async () => {
    console.log('Refreshing prayers...');
    await refreshPrayers();
  };

  // Get prayer counts for tabs
  const getPrayerCounts = () => ({
    my: userPrayers.length,
    community: communityPrayers.length,
    favorites: favoritePrayers.length,
    bookmarks: bookmarkedPrayers.length,
  });

  return {
    // Prayer data
    userPrayers,
    communityPrayers,
    favoritePrayers,
    bookmarkedPrayers,
    currentPrayers: getCurrentPrayers(),
    
    // UI state
    activeTab,
    setActiveTab,
    isLoading,
    error,
    
    // Actions
    handlePray,
    handleLike,
    handleComment,
    handleBookmark,
    handleRefresh,
    
    // Utilities
    getPrayerCounts,
  };
}
